import { useEffect, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import {
  apiGet,
  type Activity,
  type Appointment,
  type Conversation,
  type Patient,
  type Payment,
} from "../api/client";
import { ActivityBrick } from "../components/ActivityBrick";
import { ChatThread } from "../components/ChatThread";
import { EmptyMessage } from "../components/EmptyMessage";
import { FilterDropdown } from "../components/FilterDropdown";
import { formatCurrency, formatDayShort, formatTimeParts } from "../lib/format";

type Ctx = { patient: Patient };

/**
 * Patient activity tab — historical timeline include
 * (`patients/activity.html` → `activity/timeline.html`) with dropdown chrome.
 */
export function PatientActivityTab() {
  const { patient } = useOutletContext<Ctx>();
  const [items, setItems] = useState<Activity[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [key, setKey] = useState("All");

  // Always query by resolved patient.id — URL may use demo-patient alias.
  useEffect(() => {
    if (!patient?.id) return;
    let cancelled = false;
    setLoading(true);
    apiGet<
      Array<{ activities: Activity[]; total?: number; counts?: Record<string, number> }>
    >("/timelines", {
      patient_id: patient.id,
      key,
      page: 1,
      per_page: 15,
    })
      .then((d) => {
        if (cancelled) return;
        setItems(d?.[0]?.activities ?? []);
        setTotal(d?.[0]?.total ?? d?.[0]?.activities?.length ?? 0);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [patient?.id, key]);

  return (
    <div className="ne__patient__tab ne__patient__tab--activity">
      <div className="ne__include ne__include--grow">
        <ul className="ne__timeline__dropdowns">
          <div className="ne__timeline__dropdowns-padding">
            <li className="ne__timeline__activity-type">
              <FilterDropdown
                value={key}
                onChange={setKey}
                badgeCount={total}
                options={[
                  { key: "All", title: "All activity", count: total },
                  {
                    key: "Appointment Cancelled",
                    title: "Appt. cancellations",
                  },
                  {
                    key: "Message Received",
                    title: "Messages",
                  },
                  {
                    key: "Appointment Requested",
                    title: "Appointment requested",
                  },
                  {
                    key: "Survey Submission Created",
                    title: "Surveys",
                  },
                ]}
              />
            </li>
            <li className="ne__timeline__providers">
              <FilterDropdown
                value="all"
                onChange={() => {}}
                options={[{ key: "all", title: "All providers" }]}
              />
            </li>
          </div>
        </ul>

        {loading ? (
          <EmptyMessage>
            <p>Loading activity…</p>
          </EmptyMessage>
        ) : null}

        {!loading && items.length === 0 ? (
          <EmptyMessage
            title={`No recent activity from ${patient.first_name} ${patient.last_name}.`}
          />
        ) : null}

        {!loading && items.length > 0 ? (
          <ul className="ne__timeline">
            {items.map((item) => (
              <ActivityBrick key={item.id} item={item} />
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
}

const APPT_PAGE_SIZES = [10, 25, 50, 100] as const;

export function PatientAppointmentsTab() {
  const { patient } = useOutletContext<Ctx>();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  // ng-table default counts control (see ng-table/pager.html)
  const [pageSize, setPageSize] = useState<(typeof APPT_PAGE_SIZES)[number]>(10);

  useEffect(() => {
    if (!patient?.id) return;
    let cancelled = false;
    apiGet<Appointment[]>("/appointments", { patient_id: patient.id })
      .then((data) => {
        if (!cancelled) setAppointments(data || []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [patient?.id]);

  if (loading) {
    return (
      <div className="ne__empty-message">
        <p>Loading appointments…</p>
      </div>
    );
  }

  if (!appointments.length) {
    return (
      <div className="ne__empty-message">
        <h2>
          No appointments scheduled with {patient.first_name} {patient.last_name}.
        </h2>
      </div>
    );
  }

  const visible = appointments.slice(0, pageSize);

  return (
    <div className="ne__patient__tab ne__patient__tab--appointments">
      {/* Historical patients/tabs/appointments.html + ng-table pager counts */}
      <div className="ne__patient-appts__table">
        <div className="ne__table__padding">
          <table className="ne__table">
            <thead>
              <tr>
                <th>
                  <h6 className="ne__table__eyebrow">Notes</h6>
                </th>
                <th>
                  <h6 className="ne__table__eyebrow">Appointment</h6>
                </th>
                <th>
                  <h6 className="ne__table__eyebrow">Status</h6>
                </th>
              </tr>
            </thead>
            <tbody>
              {visible.map((appt) => {
                const type =
                  typeof appt.appointment_type === "object"
                    ? appt.appointment_type
                    : {
                        name: String(appt.appointment_type || ""),
                        minutes: undefined,
                      };
                const { time, period } = formatTimeParts(appt.start_time);
                const start = appt.start_time
                  ? new Date(appt.start_time)
                  : null;
                const dayLabel =
                  start && !Number.isNaN(start.getTime())
                    ? start.toLocaleDateString("en-US", {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                    : "";
                // Angular: EEE MMM d, yyyy → "Mon Aug 3, 2026" (no comma after weekday)
                const dayAngular = dayLabel.replace(/^(\w+),/, "$1");
                const statusLabel =
                  appt.status === "patient_missed"
                    ? "No-Show"
                    : (appt.status || "").replace(/^\w/, (c) =>
                        c.toUpperCase(),
                      );
                return (
                  <tr key={appt.id}>
                    <td>
                      {type.name ? (
                        <p className="ne__patient-appts__notes">
                          <strong>{type.name}</strong>
                          {type.minutes != null ? (
                            <small> {type.minutes}</small>
                          ) : null}
                        </p>
                      ) : null}
                      {appt.note ? (
                        <p className="ne__patient-appts__notes">{appt.note}</p>
                      ) : null}
                    </td>
                    <td>
                      <p className="ne__patient-appts__time">
                        <span>{dayAngular}</span>
                        {time ? (
                          <>
                            {/* Angular template: <span>at</span> with no surrounding spaces in HTML; text nodes add them via CSS whitespace */}
                            <span> at </span>
                            <span>{time}</span>
                            <span className="ne__period"> {period}</span>
                          </>
                        ) : null}
                      </p>
                    </td>
                    <td>
                      <div className="ne__patient-appts__status">
                        {/* Angular only paints icons for upcoming / paid / cancelled|missed|unpaid */}
                        <span className="ne__patient-appts__status-icons">
                          {appt.status === "upcoming" ? (
                            <i className="ne__icon__waiting--thin" />
                          ) : null}
                          {appt.status === "paid" ? (
                            <i className="ne__icon__tick--thin" />
                          ) : null}
                          {appt.status === "cancelled" ||
                          appt.status === "patient_missed" ||
                          appt.status === "unpaid" ? (
                            <i className="ne__icon__x--thin" />
                          ) : null}
                        </span>
                        <span>{statusLabel}</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {/* ng-table counts pager — always visible when data present */}
          <div className="ng-cloak ng-table-pager">
            <div className="ng-table-counts btn-group pull-right">
              {APPT_PAGE_SIZES.map((count) => (
                <button
                  key={count}
                  type="button"
                  className={`btn btn-default${pageSize === count ? " active" : ""}`}
                  onClick={() => setPageSize(count)}
                >
                  <span>{count}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PatientPaymentsTab() {
  const { patient } = useOutletContext<Ctx>();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!patient?.id) return;
    let cancelled = false;
    apiGet<Payment[]>("/payments", { patient_id: patient.id })
      .then((data) => {
        if (!cancelled) setPayments(data || []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [patient?.id]);

  if (loading) {
    return (
      <div className="ne__empty-message">
        <p>Loading payments…</p>
      </div>
    );
  }

  if (!payments.length) {
    return (
      <div className="ne__empty-message">
        <h2>
          No payments from {patient.first_name} {patient.last_name}.
        </h2>
      </div>
    );
  }

  return (
    <div className="ne__patient__tab ne__patient__tab--payments">
      <div className="ne__table__padding">
        <table className="ne__table">
          <thead>
            <tr>
              <th>
                <h6 className="ne__table__eyebrow">Payment made at</h6>
              </th>
              <th>
                <h6 className="ne__table__eyebrow">Amount</h6>
              </th>
              <th>
                <h6 className="ne__table__eyebrow">Payment status</h6>
              </th>
              <th>
                <h6 className="ne__table__eyebrow">Open appointment</h6>
              </th>
            </tr>
          </thead>
          <tbody>
            {payments.map((pmt) => (
              <tr key={pmt.id}>
                <td>
                  {formatDayShort(pmt.created_at)}{" "}
                  {(() => {
                    const p = formatTimeParts(pmt.created_at);
                    return (
                      <>
                        <span className="ne__time">{p.time}</span>
                        <span className="ne__period">{p.period}</span>
                      </>
                    );
                  })()}
                </td>
                <td>{formatCurrency(pmt.amount)}</td>
                <td style={{ textTransform: "capitalize" }}>{pmt.status}</td>
                <td>
                  <span className="ne__link ne__link--primary">
                    Open appointment
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function PatientConversationTab() {
  const { conversationId } = useParams();
  const [conversation, setConversation] = useState<Conversation | null>(null);

  useEffect(() => {
    if (!conversationId) return;
    let cancelled = false;
    apiGet<Conversation[]>(`/conversations/${conversationId}`).then((data) => {
      if (!cancelled) {
        setConversation(Array.isArray(data) ? data[0] : data);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [conversationId]);

  if (!conversation) {
    return (
      <EmptyMessage>
        <p>Loading conversation…</p>
      </EmptyMessage>
    );
  }

  return (
    <ChatThread
      messages={conversation.messages_list || []}
      showComposer
      onSend={() => {
        /* archive is read-only */
      }}
    />
  );
}

type Medication = {
  id: string;
  name: string;
  dosage?: string;
  patient_id?: string;
  prescribed_at?: string;
};

/** Patient medications (`medication-list.html` / app.patient.medications). */
export function PatientMedicationsTab() {
  const { patient } = useOutletContext<Ctx>();
  const [meds, setMeds] = useState<Medication[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!patient?.id) return;
    let cancelled = false;
    apiGet<Medication[]>("/medications", { patient_id: patient.id })
      .then((d) => {
        if (!cancelled) setMeds(Array.isArray(d) ? d : []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [patient?.id]);

  if (loading) {
    return (
      <EmptyMessage>
        <p>Loading medications…</p>
      </EmptyMessage>
    );
  }

  if (!meds.length) {
    return (
      <EmptyMessage
        title={`No medications on file for ${patient.first_name} ${patient.last_name}.`}
      />
    );
  }

  return (
    <div className="ne__patient__tab ne__patient__tab--medications">
      <div className="ne__table__padding">
        <table className="ne__table">
          <thead>
            <tr>
              <th className="ne__table__eyebrow">Drug</th>
              <th className="ne__table__eyebrow">Dosage</th>
              <th className="ne__table__eyebrow">Prescribed</th>
            </tr>
          </thead>
          <tbody>
            {meds.map((med) => {
              const { time, period } = formatTimeParts(med.prescribed_at);
              return (
                <tr key={med.id}>
                  <td>{med.name}</td>
                  <td>{med.dosage || "—"}</td>
                  <td>
                    {med.prescribed_at ? formatDayShort(med.prescribed_at) : "—"}
                    {time ? (
                      <>
                        {" "}
                        at {time}
                        <span className="ne__period"> {period}</span>
                      </>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

type DocumentRow = {
  id: string;
  title?: string;
  name?: string;
  file_name?: string;
  attachment_url?: string;
  patient_id?: string;
  created_at?: string;
};

/** Patient documents (`documents.html` / app.patient.documents). */
export function PatientDocumentsTab() {
  const { patient } = useOutletContext<Ctx>();
  const [docs, setDocs] = useState<DocumentRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!patient?.id) return;
    let cancelled = false;
    apiGet<DocumentRow[]>("/documents", { patient_id: patient.id })
      .then((d) => {
        if (!cancelled) setDocs(Array.isArray(d) ? d : []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [patient?.id]);

  if (loading) {
    return (
      <EmptyMessage>
        <p>Loading documents…</p>
      </EmptyMessage>
    );
  }

  if (!docs.length) {
    return (
      <EmptyMessage
        title={`No documents uploaded for ${patient.first_name} ${patient.last_name}.`}
      />
    );
  }

  return (
    <div className="ne__patient__tab ne__patient__tab--documents">
      <div className="ne__document-sidebar" style={{ padding: "1rem 0" }}>
        <h4 className="page-header mt0">Uploaded Files</h4>
        <ul style={{ listStyle: "none", padding: 0 }}>
          {docs.map((file) => {
            const label =
              file.title || file.name || file.file_name || "Document";
            return (
              <li key={file.id} style={{ marginBottom: "0.5rem" }}>
                {file.attachment_url ? (
                  <a href={file.attachment_url} target="_blank" rel="noreferrer">
                    {label}
                  </a>
                ) : (
                  <span>{label}</span>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
